// Admin Reservation Management — full CRM view
// Shows vehicle, guest contact, dates, status, line items, and state-appropriate actions

import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { adminApi, reservationApi } from '../../lib/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = [
  { value: '', label: '전체' },
  { value: 'requested', label: '🔔 검토 대기' },
  { value: 'host_approved', label: '✅ 호스트 승인' },
  { value: 'payment_pending', label: '💳 견적 확정' },
  { value: 'payment_authorized', label: '💳 카드 등록 완료' },
  { value: 'confirmed', label: '📋 예약 확정' },
  { value: 'in_progress', label: '🚗 대여 중' },
  { value: 'completed', label: '🏁 반납 완료' },
  { value: 'payment_captured', label: '💰 정산 완료' },
  { value: 'settled', label: '✓ 종료' },
  { value: 'cancelled', label: '❌ 취소' },
  { value: 'rejected_by_host', label: '↩ 호스트 거절' },
  { value: 'rejected_by_admin', label: '↩ 관리자 거절' },
];

const STATUS_LABEL: Record<string, string> = {
  draft: '작성중', requested: '검토 대기', host_approved: '호스트 승인',
  payment_pending: '견적 확정', payment_authorized: '카드 등록 완료',
  confirmed: '예약 확정', in_progress: '대여 중', completed: '반납 완료',
  payment_captured: '정산 완료', settled: '종료',
  cancelled: '취소됨', rejected_by_host: '호스트 거절', rejected_by_admin: '관리자 거절',
};

const ITEM_LABEL: Record<string, string> = {
  rental: '대여료', platform_fee: '수수료', care: '현장케어',
  transport: '운송비', shuttle: '셔틀', insurance: '보험', other: '기타',
};

const EVENT_LABEL: Record<string, string> = {
  requested: '예약 신청', host_approved: '호스트 승인', host_accept: '호스트 승인',
  admin_approve: '관리자 승인', payment_pending: '견적 확정',
  payment_authorized: '카드 등록', confirmed: '예약 확정',
  pickup: '차량 인도', in_progress: '대여 시작',
  return: '차량 반납', completed: '반납 완료',
  capture: '카드 청구', payment_captured: '청구 완료',
  settle: '정산 완료', settled: '정산 완료',
  cancelled: '예약 취소', cancel: '예약 취소',
  rejected_by_host: '호스트 거절', host_reject: '호스트 거절',
  rejected_by_admin: '관리자 거절', admin_reject: '관리자 거절',
  created: '예약 생성', submit: '예약 제출',
};

export default function AdminReservationsPage() {
  const [searchParams] = useSearchParams();

  // Read expand ID from URL query param (passed by dashboard cards)
  // Read fresh on EVERY render — no effect timing issues
  const expandFromUrl = searchParams.get('expand') || undefined;

  const [reservations, setReservations] = useState<any[]>([]);
  const [status, setStatus] = useState(() => expandFromUrl ? '' : 'requested');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, total_pages: 0 });
  // Initialize expandedId from URL param so it's set BEFORE first render
  const [expandedId, setExpandedId] = useState<string | null>(expandFromUrl || null);
  const [userToggled, setUserToggled] = useState(false); // true once user clicks any accordion
  const [confirmPrice, setConfirmPrice] = useState<Record<string, string>>({});
  const [confirmNotes, setConfirmNotes] = useState<Record<string, string>>({});

  const load = (p?: number) => {
    setLoading(true);
    const params: any = {};
    if (status) params.status = status;
    params.page = String(p || page);
    if (expandFromUrl) params.expand = expandFromUrl;
    adminApi.reservations(params)
      .then((res: any) => {
        setReservations(res.reservations || []);
        setMeta(res.meta || { total: 0, total_pages: 0 });
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  useEffect(() => { setPage(1); load(1); }, [status]);

  const goPage = (p: number) => {
    setPage(p);
    setExpandedId(null);
    setUserToggled(false);
    load(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Auto-scroll to expanded reservation from dashboard
  useEffect(() => {
    if (expandFromUrl && !loading && reservations.length > 0) {
      // Scroll to top since target is reordered to first position
      const el = document.getElementById(`res-${expandFromUrl}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Fallback: scroll to top of list
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, [expandFromUrl, loading, reservations]);

  const formatPrice = (won: number) => won ? new Intl.NumberFormat('ko-KR').format(won) + '원' : '미정';
  const formatDate = (d: string) => {
    if (!d) return '-';
    const dt = new Date(d);
    const day = ['일','월','화','수','목','금','토'][dt.getDay()];
    return `${dt.getMonth()+1}월 ${dt.getDate()}일 (${day})`;
  };

  const handleHostApprove = async (id: string) => {
    try {
      await adminApi.hostApproveReservation(id);
      toast.success('호스트 승인 완료');
      load();
      setExpandedId(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleAdminApprove = async (id: string) => {
    const price = confirmPrice[id];
    const notes = confirmNotes[id];
    try {
      await adminApi.approveReservation(id, {
        notes: notes || undefined,
        total_amount: price ? parseInt(price) : undefined,
      });
      toast.success('견적 확정 완료');
      setConfirmPrice(prev => { const n = {...prev}; delete n[id]; return n; });
      setConfirmNotes(prev => { const n = {...prev}; delete n[id]; return n; });
      load();
      setExpandedId(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const handleReject = async (id: string) => {
    const reason = prompt('거절 사유를 입력하세요:');
    if (!reason) return;
    try {
      await adminApi.rejectReservation(id, reason);
      toast.success('거절 완료');
      load();
      setExpandedId(null);
    } catch (err: any) { toast.error(err.message); }
  };

  const totalAmount = (items: any[]) =>
    items?.reduce((s: number, li: any) => s + (li.total_amount || 0), 0) || 0;

// --- Inline helper components ---

function CostCalculator({ reservationId, lineItems, onTotalChange }: { reservationId: string; lineItems: any[]; onTotalChange?: (total: number) => void }) {
  const [items, setItems] = useState(lineItems.map(li => ({
    ...li,
    _editAmount: li.total_amount || li.amount || 0,
  })));
  const [newItemDesc, setNewItemDesc] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');

  const total = items.reduce((s, li) => s + (li._editAmount || 0), 0);

  useEffect(() => { onTotalChange?.(total); }, [total]);

  const updateItem = (idx: number, amount: number) => {
    const updated = [...items];
    updated[idx]._editAmount = amount;
    setItems(updated);
  };

  const addItem = () => {
    if (!newItemDesc || !newItemAmount) return;
    setItems([...items, {
      id: `new-${Date.now()}`,
      item_type: 'other',
      description: newItemDesc,
      _editAmount: parseInt(newItemAmount),
      total_amount: parseInt(newItemAmount),
      amount: parseInt(newItemAmount),
      tax_amount: 0,
    }]);
    setNewItemDesc('');
    setNewItemAmount('');
  };

  const formatPrice = (won: number) => new Intl.NumberFormat('ko-KR').format(won) + '원';

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <h5 className="text-sm font-medium text-gray-700 mb-2">🧮 비용 계산기</h5>
      <div className="space-y-1.5 mb-2">
        {items.map((li, i) => (
          <div key={li.id} className="flex items-center gap-2 text-xs">
            <span className="text-gray-500 w-16 shrink-0">{li.item_type === 'rental' ? '대여료' : li.item_type === 'care' ? '케어' : li.item_type === 'transport' ? '운송' : '기타'}</span>
            <span className="text-gray-400 flex-1 truncate">{li.description || ''}</span>
            <input type="number" className="input-field text-xs w-28 text-right"
              value={li._editAmount || ''} onChange={e => updateItem(i, parseInt(e.target.value) || 0)} />
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-2">
        <input type="text" className="input-field text-xs flex-1" placeholder="항목 추가"
          value={newItemDesc} onChange={e => setNewItemDesc(e.target.value)} />
        <input type="number" className="input-field text-xs w-28" placeholder="금액"
          value={newItemAmount} onChange={e => setNewItemAmount(e.target.value)} />
        <button onClick={addItem} className="px-2 py-1 bg-gray-100 hover:bg-gray-200 text-xs rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">+</button>
      </div>
      <div className="flex justify-between font-semibold text-sm border-t border-gray-100 pt-2">
        <span>최종 청구 금액</span>
        <span className="text-slate-900">{formatPrice(total)}</span>
      </div>
    </div>
  );
}

function ChargeForm({ reservationId, cardLastFour, estimatedAmount }: { reservationId: string; cardLastFour?: string; estimatedAmount: number }) {
  const [amount, setAmount] = useState(String(estimatedAmount || ''));
  const [orderName, setOrderName] = useState(`대여 정산 #${reservationId.slice(0, 8)}`);
  const [charging, setCharging] = useState(false);

  const handleCharge = async () => {
    if (!amount || parseInt(amount) <= 0) { toast.error('금액을 입력하세요'); return; }
    setCharging(true);
    try {
      await reservationApi.chargePayment(reservationId, {
        amount: parseInt(amount),
        orderName: orderName || undefined,
      });
      toast.success('카드 청구 완료');
      setAmount('');
    } catch (err: any) { toast.error(err.message); }
    finally { setCharging(false); }
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="number" className="input-field text-sm sm:w-36 w-full" placeholder="청구 금액"
          value={amount} onChange={e => setAmount(e.target.value)} />
        <input type="text" className="input-field text-sm flex-1" placeholder="청구 항목명"
          value={orderName} onChange={e => setOrderName(e.target.value)} />
      </div>
      {cardLastFour && <p className="text-xs text-gray-400">등록 카드: ****{cardLastFour}</p>}
      {!cardLastFour && <p className="text-xs text-amber-500">⚠️ 등록된 카드가 없습니다. 게스트가 먼저 카드를 등록해야 합니다.</p>}
      <button onClick={handleCharge} disabled={charging || !amount || !cardLastFour}
        className="min-h-[44px] px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
        {charging ? '처리 중...' : `카드 청구 (${amount ? new Intl.NumberFormat('ko-KR').format(parseInt(amount)) + '원' : '금액 입력'})`}
      </button>
    </div>
  );
}

function TaxInvoiceButton({ reservationId, guestName, guestCompany }: { reservationId: string; guestName: string; guestCompany?: string }) {
  const [confirmed, setConfirmed] = useState(false);
  const [asking, setAsking] = useState(false);

  const handleConfirm = async () => {
    setAsking(true);
  };

  const doConfirm = async () => {
    try {
      const token = localStorage.getItem('carp2p_token');
      const r = await fetch(`/api/admin/tax-invoices`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ reservation_id: reservationId }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.error || '발행 실패');
      }
      setConfirmed(true);
      toast.success('세금계산서 발행 확인 완료');
    } catch (err: any) { toast.error(err.message); }
    finally { setAsking(false); }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <h5 className="text-sm font-medium text-gray-700 mb-2">🧾 세금계산서 발행 확인</h5>
      {confirmed ? (
        <div className="text-xs text-emerald-700 bg-emerald-50 rounded-lg p-2 flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          세금계산서 발행 확인 완료
        </div>
      ) : asking ? (
        <div className="bg-amber-50 rounded-lg p-3 text-sm">
          <p className="text-amber-800 mb-2">
            홈택스에서 <strong>{guestCompany || guestName}</strong>에게 세금계산서를 발행하셨습니까?
          </p>
          <p className="text-xs text-amber-600 mb-3">※ 실제 세금계산서 발행은 홈택스(www.hometax.go.kr)에서 진행하세요.</p>
          <div className="flex gap-2">
            <button onClick={doConfirm}
              className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              발행 완료
            </button>
            <button onClick={() => setAsking(false)}
              className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-600 text-xs font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              취소
            </button>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-xs text-gray-400 mb-2">
            {guestCompany || guestName} · 홈택스에서 발행 후 확인 버튼을 눌러주세요.
          </p>
          <button onClick={handleConfirm}
            className="px-4 py-2 border-2 border-gray-300 hover:border-emerald-400 text-gray-600 text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
            📋 세금계산서 발행 확인
          </button>
        </div>
      )}
    </div>
  );
}

// --- Main component ---

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-xl sm:text-2xl font-bold mb-2">예약 관리</h1>
      <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6">
        게스트 문의 → 호스트 승인 → 견적 확정 → 카드등록 → 인도 → 반납 → 정산
      </p>

      {/* Status tabs — wrap naturally on mobile */}
      <div className="flex flex-wrap gap-1 sm:gap-1.5 mb-4 sm:mb-5">
        {STATUS_OPTIONS.map(o => (
          <button
            key={o.value}
            onClick={() => setStatus(o.value)}
            className={`min-h-[44px] px-3 py-1.5 rounded-lg text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              status === o.value
                ? 'bg-slate-900 text-white shadow-sm'
                : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Reservation list */}
      {reservations.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-400">
          예약 내역이 없습니다
        </div>
      ) : (
        <div className="space-y-3">
          {(expandFromUrl
            ? [...reservations].sort((a, b) => (a.id === expandFromUrl ? -1 : b.id === expandFromUrl ? 1 : 0))
            : reservations
          ).map((r: any) => {
            const isExpanded = userToggled ? (expandedId === r.id) : (expandFromUrl === r.id || expandedId === r.id);
            const total = r.line_items ? totalAmount(r.line_items) : r.total_amount;

            return (
              <div
                id={`res-${r.id}`}
                key={r.id}
                className={`bg-white rounded-2xl border shadow-sm transition-all ${
                  isExpanded ? 'border-brand-300 ring-2 ring-brand-100' : 'border-gray-100 hover:border-gray-200'
                }`}
              >
                {/* Summary row — always visible */}
                <button
                  onClick={() => { setUserToggled(true); setExpandedId(isExpanded ? null : r.id); }}
                  className="w-full p-3 sm:p-4 text-left focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-inset rounded-2xl"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1.5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 sm:gap-2 mb-1 flex-wrap">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          r.status === 'requested' ? 'bg-amber-100 text-amber-700' :
                          r.status === 'host_approved' ? 'bg-blue-100 text-blue-700' :
                          r.status === 'payment_pending' ? 'bg-purple-100 text-purple-700' :
                          r.status === 'in_progress' ? 'bg-green-100 text-green-700' :
                          r.status === 'completed' || r.status === 'settled' ? 'bg-gray-100 text-gray-500' :
                          r.status.includes('rejected') || r.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {STATUS_LABEL[r.status] || r.status}
                        </span>
                        <span className="font-semibold text-slate-900">
                          {r.vehicles?.length ? (
                            r.vehicles.length === 1
                              ? `${r.vehicles[0].make} ${r.vehicles[0].model} (${r.vehicles[0].year})`
                              : `${r.vehicles[0].make} ${r.vehicles[0].model} 외 ${r.vehicles.length - 1}대`
                          ) : (
                            `${r.vehicle_make || ''} ${r.vehicle_model || ''} (${r.vehicle_year || ''})`
                          )}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-gray-500">
                        <span>{r.guest_name}</span>
                        {r.guest_company && <span className="text-gray-300">|</span>}
                        {r.guest_company && <span>{r.guest_company}</span>}
                        <span className="text-gray-300">|</span>
                        <span className="break-words">{formatDate(r.pickup_date)} ~ {formatDate(r.return_date)} ({r.rental_days}일)</span>
                        <span className="text-gray-300">|</span>
                        <span className="font-medium text-slate-700">{formatPrice(total)}</span>
                        <span className="text-gray-300">|</span>
                        <span className="text-xs text-gray-400">신청 {r.created_at ? formatDate(r.created_at) : '-'}</span>
                      </div>
                    </div>
                    <svg className={`w-5 h-5 text-gray-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </button>

                {/* Expanded detail */}
                {isExpanded && (
                  <div className="px-4 pb-5 border-t border-gray-50 pt-4">
                    {/* Guest + Contact Info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-4">
                      <div className="bg-gray-50 rounded-xl p-3">
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">게스트 정보</h4>
                        <p className="text-sm font-medium text-slate-900">{r.guest_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">{r.guest_email}</p>
                        <p className="text-xs text-gray-500">{r.guest_phone}</p>
                        {r.guest_company && <p className="text-xs text-gray-500 mt-1">🏢 {r.guest_company}</p>}
                      </div>
                      <div className="bg-gray-50 rounded-xl p-3">
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">차량 정보</h4>
                        {r.vehicles?.length ? (
                          r.vehicles.map((v: any, i: number) => (
                            <div key={v.id || i} className={i > 0 ? 'mt-2 pt-2 border-t border-gray-200' : ''}>
                              <p className="text-sm font-medium text-slate-900">{v.make} {v.model} ({v.year})</p>
                              <p className="text-xs text-gray-500">
                                {v.vehicle_type} · {v.category === 'prop_car' ? '촬영용' : '렌트카'}
                                {v.daily_rate ? ` · ${formatPrice(v.daily_rate)}/일` : v.daily_rate_snapshot ? ` · ${formatPrice(v.daily_rate_snapshot)}/일` : ''}
                              </p>
                              {v.host_name && <p className="text-xs text-gray-500">차주: {v.host_name}</p>}
                            </div>
                          ))
                        ) : (
                          <>
                            <p className="text-sm font-medium text-slate-900">{r.vehicle_make} {r.vehicle_model} ({r.vehicle_year})</p>
                            <p className="text-xs text-gray-500">
                              {r.vehicle_type} · {r.category === 'prop_car' ? '촬영용' : '렌트카'}
                              {r.vehicle_daily_rate ? ` · ${formatPrice(r.vehicle_daily_rate)}/일` : ''}
                            </p>
                            <p className="text-xs text-gray-500">차주: {r.host_name}</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Dates + Notes */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 mb-4 text-xs sm:text-sm">
                      <div>
                        <span className="text-xs text-gray-400">인도일</span>
                        <p className="font-medium">{formatDate(r.pickup_date)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">반납일</span>
                        <p className="font-medium">{formatDate(r.return_date)}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">대여 기간</span>
                        <p className="font-medium">{r.rental_days}일</p>
                      </div>
                    </div>

                    {/* Guest notes / Host notes */}
                    {(r.guest_notes || r.host_notes || r.admin_notes) && (
                      <div className="space-y-1.5 mb-4 text-xs">
                        {r.guest_notes && (
                          <div className="bg-blue-50 rounded-lg p-2.5">
                            <span className="font-medium text-blue-500">게스트 요청:</span>
                            <span className="text-blue-800 ml-1">{r.guest_notes}</span>
                          </div>
                        )}
                        {r.host_notes && (
                          <div className="bg-green-50 rounded-lg p-2.5">
                            <span className="font-medium text-green-500">호스트 메모:</span>
                            <span className="text-green-800 ml-1">{r.host_notes}</span>
                          </div>
                        )}
                        {r.admin_notes && (
                          <div className="bg-purple-50 rounded-lg p-2.5">
                            <span className="font-medium text-purple-500">관리자 메모:</span>
                            <span className="text-purple-800 ml-1">{r.admin_notes}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Line items */}
                    {r.line_items?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">비용 항목</h4>
                        <div className="border border-gray-100 rounded-lg divide-y divide-gray-50 text-sm">
                          {r.line_items.map((li: any) => (
                            <div key={li.id} className="flex justify-between p-2">
                              <span className="text-gray-600">
                                {ITEM_LABEL[li.item_type] || li.item_type} {li.description || ''}
                              </span>
                              <span className="font-medium">{formatPrice(li.total_amount)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between p-2 bg-gray-50 font-semibold">
                            <span>합계</span>
                            <span>{formatPrice(total)}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Event timeline */}
                    {r.events?.length > 0 && (
                      <div className="mb-4">
                        <h4 className="text-xs font-semibold text-gray-400 uppercase mb-2">진행 이력</h4>
                        <div>
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="text-gray-400 border-b border-gray-100">
                                <th className="text-left py-1.5 pr-2 font-medium">시간</th>
                                <th className="text-left py-1.5 pr-2 font-medium">이벤트</th>
                                <th className="text-left py-1.5 font-medium">상태 변경</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50">
                              {r.events.map((ev: any) => (
                                <tr key={ev.id} className="text-gray-600">
                                  <td className="py-1.5 pr-2 text-gray-400">
                                    {ev.created_at ? new Date(ev.created_at).toLocaleString('ko-KR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                                  </td>
                                  <td className="py-1.5 pr-2 font-medium">
                                    {EVENT_LABEL[ev.event_type] || ev.event_type}
                                  </td>
                                  <td className="py-1.5">
                                    <span className="text-gray-400">{STATUS_LABEL[ev.from_status] || ev.from_status}</span>
                                    <span className="mx-1 text-gray-300">→</span>
                                    <span>{STATUS_LABEL[ev.to_status] || ev.to_status}</span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Action buttons — context-dependent */}
                    <div className="flex items-end gap-2 flex-wrap pt-2 border-t border-gray-100 text-xs sm:text-sm">
                      {r.status === 'requested' && (
                        <>
                          <button onClick={() => handleHostApprove(r.id)}
                            className="min-h-[44px] px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                            호스트 승인
                          </button>
                          <button onClick={() => handleReject(r.id)}
                            className="min-h-[44px] px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                            거절
                          </button>
                        </>
                      )}

                      {r.status === 'host_approved' && (
                        <div className="w-full space-y-3">
                          <div className="flex flex-col sm:flex-row gap-2 sm:items-end">
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">확정 금액 (원)</label>
                              <input
                                type="number"
                                className="input-field text-sm sm:w-40 w-full"
                                placeholder={r.total_amount ? formatPrice(r.total_amount) : '금액 입력'}
                                value={confirmPrice[r.id] || ''}
                                onChange={e => setConfirmPrice(p => ({...p, [r.id]: e.target.value}))}
                              />
                            </div>
                            <div className="flex-1">
                              <label className="text-xs text-gray-500 block mb-1">메모</label>
                              <input
                                type="text"
                                className="input-field text-sm"
                                placeholder="협의 내용"
                                value={confirmNotes[r.id] || ''}
                                onChange={e => setConfirmNotes(p => ({...p, [r.id]: e.target.value}))}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button onClick={() => handleAdminApprove(r.id)}
                              className="min-h-[44px] px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                              견적 확정 (최종 승인)
                            </button>
                            <button onClick={() => handleReject(r.id)}
                              className="min-h-[44px] px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                              거절
                            </button>
                          </div>
                        </div>
                      )}

                      {r.status === 'payment_pending' && (
                        <div className="w-full space-y-3">
                          <div className="text-sm text-purple-600 bg-purple-50 rounded-lg p-3">
                            💳 게스트의 카드 등록을 기다리는 중입니다. 견적이 확정되었습니다.
                          </div>
                          {/* Show confirmed line items, allow editing */}
                          <div className="border border-gray-200 rounded-lg p-3 text-sm">
                            <h5 className="font-medium text-gray-700 mb-2">💰 확정 비용</h5>
                            {r.line_items?.map((li: any) => (
                              <div key={li.id} className="flex justify-between py-1 text-xs">
                                <span className="text-gray-500">{ITEM_LABEL[li.item_type] || li.item_type}</span>
                                <span>{formatPrice(li.total_amount)}</span>
                              </div>
                            ))}
                            <div className="flex justify-between py-1 border-t border-gray-100 mt-1 pt-1 font-semibold text-sm">
                              <span>청구 예정</span>
                              <span>{formatPrice(total)}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {(r.status === 'confirmed' || r.status === 'in_progress') && (
                        <div className="w-full space-y-3">
                          <div className="text-sm text-blue-600 bg-blue-50 rounded-lg p-3">
                            {r.status === 'in_progress' ? '🚗 대여 중' : '📋 예약 확정'} · 인도일: {formatDate(r.pickup_date)} · 반납일: {formatDate(r.return_date)}
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <a href={`/reservations/${r.id}/inspection`} target="_blank" rel="noreferrer"
                              className="min-h-[44px] inline-flex items-center gap-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                              📸 사진 촬영/업로드
                            </a>
                            {r.status === 'confirmed' && (
                              <button onClick={async () => {
                                if (!confirm('차량 인도를 확인하시겠습니까?\n\n확인 시 예약이 "대여 중" 상태로 변경됩니다.')) return;
                                try {
                                  const token = localStorage.getItem('carp2p_token');
                                  await fetch(`/api/reservations/${r.id}/transition`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ event: 'pickup', notes: '관리자 인도 확인' }),
                                  });
                                  toast.success('차량 인도 완료 — 대여 중 상태로 변경됨');
                                  load();
                                  setExpandedId(null);
                                } catch (err: any) { toast.error(err.message); }
                              }}
                              className="min-h-[44px] px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                              🚗 차량 인도 확인
                              </button>
                            )}
                            {r.status === 'in_progress' && (
                              <button onClick={async () => {
                                if (!confirm('차량 반납을 확인하시겠습니까?\n\n확인 시 예약이 "반납 완료" 상태로 변경됩니다.')) return;
                                try {
                                  const token = localStorage.getItem('carp2p_token');
                                  await fetch(`/api/reservations/${r.id}/transition`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                                    body: JSON.stringify({ event: 'return', notes: '관리자 반납 확인' }),
                                  });
                                  toast.success('반납 처리 완료');
                                  load();
                                  setExpandedId(null);
                                } catch (err: any) { toast.error(err.message); }
                              }}
                              className="min-h-[44px] px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
                              🏁 반납 처리
                              </button>
                            )}
                          </div>
                        </div>
                      )}

                      {r.status === 'payment_authorized' && (
                        <div className="w-full space-y-3">
                          <div className="text-sm text-green-600 bg-green-50 rounded-lg p-3">
                            ✅ 카드 {r.card_last_four || '****'} 등록 완료. 인도일: {formatDate(r.pickup_date)}
                          </div>
                          {/* Card charge button */}
                          <div className="border border-gray-200 rounded-lg p-3">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">💳 카드 사후 청구</h5>
                            <p className="text-xs text-gray-400 mb-2">반납 완료 후 최종 금액으로 청구합니다.</p>
                            <ChargeForm reservationId={r.id} cardLastFour={r.card_last_four} estimatedAmount={total} />
                          </div>
                        </div>
                      )}

                      {r.status === 'completed' && (
                        <div className="w-full space-y-3">
                          <div className="text-sm text-blue-600 bg-blue-50 rounded-lg p-3">
                            🏁 반납 완료. 아래에서 최종 비용을 확정하고 청구하세요.
                          </div>
                          {/* Cost calculator — editable line items */}
                          <CostCalculator reservationId={r.id} lineItems={r.line_items || []} />
                          {/* Card charge */}
                          <div className="border border-gray-200 rounded-lg p-3">
                            <h5 className="text-sm font-medium text-gray-700 mb-2">💳 카드 최종 청구</h5>
                            <ChargeForm reservationId={r.id} cardLastFour={r.card_last_four} estimatedAmount={total} />
                          </div>
                          {/* Tax invoice */}
                          <TaxInvoiceButton reservationId={r.id} guestName={r.guest_name} guestCompany={r.guest_company} />
                        </div>
                      )}

                      {r.status === 'payment_captured' && (
                        <div className="w-full space-y-2">
                          <div className="text-sm text-emerald-600 bg-emerald-50 rounded-lg p-3">
                            💰 정산 완료. 카드 {r.card_last_four || '****'} 청구됨.
                          </div>
                          <TaxInvoiceButton reservationId={r.id} guestName={r.guest_name} guestCompany={r.guest_company} />
                        </div>
                      )}

                      {/* Terminal states — just info */}
                      {(r.status === 'settled' || r.status === 'payment_captured' || r.status.includes('rejected')) && (
                        <div className="text-sm text-gray-400 bg-gray-50 rounded-lg p-3 w-full">
                          {STATUS_LABEL[r.status]} 완료됨
                          {r.card_last_four && <span className="ml-2">· 카드 {r.card_last_four}</span>}
                        </div>
                      )}

                      {/* ID display */}
                      <span className="text-[10px] text-gray-300 ml-auto">ID: {r.id.slice(0, 12)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {meta.total_pages > 1 && (
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <span className="text-sm text-gray-400">
            총 {meta.total}건 중 {(page - 1) * 20 + 1}–{Math.min(page * 20, meta.total)}건
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => goPage(1)} disabled={page === 1}
              className="min-h-[44px] min-w-[44px] px-2 py-1.5 text-xs rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              처음
            </button>
            <button onClick={() => goPage(Math.max(1, page - 1))} disabled={page === 1}
              className="min-h-[44px] min-w-[44px] px-2 py-1.5 text-xs rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              이전
            </button>
            {Array.from({ length: Math.min(5, meta.total_pages) }, (_, i) => {
              const start = Math.max(1, Math.min(page - 2, meta.total_pages - 4));
              const p = start + i;
              if (p > meta.total_pages) return null;
              return (
                <button key={p} onClick={() => goPage(p)}
                  className={`min-h-[44px] min-w-[44px] text-xs font-medium rounded-lg transition-colors inline-flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-brand-500 ${
                    p === page ? 'bg-slate-900 text-white' : 'hover:bg-gray-100 text-gray-600'
                  }`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => goPage(Math.min(meta.total_pages, page + 1))} disabled={page === meta.total_pages}
              className="min-h-[44px] min-w-[44px] px-2 py-1.5 text-xs rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              다음
            </button>
            <button onClick={() => goPage(meta.total_pages)} disabled={page === meta.total_pages}
              className="min-h-[44px] min-w-[44px] px-2 py-1.5 text-xs rounded-lg hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500">
              마지막
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
